import React, { useState, useRef, useEffect, KeyboardEvent, ClipboardEvent } from 'react';

interface OtpInputProps {
  length?: number;
  onComplete: (code: string) => void;
  onChange?: (code: string) => void;
  error?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  alphanumeric?: boolean; // Allow letters and numbers (for invite codes)
}

export function OtpInput({
  length = 6,
  onComplete,
  onChange,
  error = false,
  disabled = false,
  autoFocus = true,
  alphanumeric = false,
}: OtpInputProps) {
  const [otp, setOtp] = useState<string[]>(new Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Auto-focus first input on mount
  useEffect(() => {
    if (autoFocus && !disabled) {
      inputRefs.current[0]?.focus();
    }
  }, [autoFocus, disabled]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value;
    
    // Check if this is a paste operation (multiple characters)
    const cleaned = alphanumeric 
      ? value.replace(/[^A-Z0-9]/gi, '').toUpperCase()
      : value.replace(/[^0-9]/g, '');
    
    if (cleaned.length > 1) {
      // Handle paste - split the pasted value across inputs
      const digits = cleaned.slice(0, length).split('');
      const newOtp = [...otp];
      
      digits.forEach((d, i) => {
        if (index + i < length) {
          newOtp[index + i] = d;
        }
      });
      
      setOtp(newOtp);
      
      // Focus the next empty input or the last input
      const nextIndex = Math.min(index + digits.length, length - 1);
      inputRefs.current[nextIndex]?.focus();
      
      const code = newOtp.join('');
      onChange?.(code);
      
      if (newOtp.every(d => d !== '') && code.length === length) {
        onComplete(code);
      }
      return;
    }
    
    // Single character input
    const digit = cleaned.slice(0, 1);

    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);

    const code = newOtp.join('');
    onChange?.(code);

    // Auto-advance to next input
    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits are entered
    if (newOtp.every(d => d !== '') && code.length === length) {
      onComplete(code);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      // If current input is empty and backspace is pressed, move to previous input
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = alphanumeric
      ? e.clipboardData.getData('text').replace(/[^A-Z0-9]/gi, '').toUpperCase()
      : e.clipboardData.getData('text').replace(/[^0-9]/g, '');
    
    if (pastedData.length > 0) {
      const digits = pastedData.slice(0, length).split('');
      const newOtp = [...otp];
      
      // Find the first empty input or start from current index
      let startIndex = 0;
      for (let i = 0; i < length; i++) {
        if (!newOtp[i]) {
          startIndex = i;
          break;
        }
      }
      
      digits.forEach((d, i) => {
        if (startIndex + i < length) {
          newOtp[startIndex + i] = d;
        }
      });
      
      setOtp(newOtp);
      
      // Focus the next empty input or the last input
      const nextIndex = Math.min(startIndex + digits.length, length - 1);
      inputRefs.current[nextIndex]?.focus();
      
      const code = newOtp.join('');
      onChange?.(code);
      
      if (newOtp.every(d => d !== '') && code.length === length) {
        onComplete(code);
      }
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Select all text when focused for easy replacement
    e.target.select();
  };

  return (
    <div className="flex gap-3 justify-center">
      {otp.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode={alphanumeric ? "text" : "numeric"}
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={handlePaste}
          onFocus={handleFocus}
          disabled={disabled}
          className={`
            w-12 h-14 text-center text-2xl font-bold rounded-md
            border-2 transition-colors
            bg-white text-black
            focus:outline-none focus:ring-2 focus:ring-[#9B1421]/50
            disabled:opacity-50 disabled:cursor-not-allowed
            ${digit 
              ? 'border-[#00D9FF] bg-white text-black' 
              : error 
                ? 'border-[#9B1421] bg-white text-black' 
                : 'border-gray-300 bg-white text-black'
            }
          `}
        />
      ))}
    </div>
  );
}

